import { useState, useEffect, useCallback } from 'react';
import type { MonthlyShiftSlotSummary } from '../../../../domain/monthlyShiftSlot';
import type { MonthlyShiftSlotSummaryRepository } from '../../../../application/monthlyShiftSlot/ports/MonthlyShiftSlotSummaryRepository';
import { getMonthlyShiftSlotSummaryList } from '../../../../application/monthlyShiftSlot';

/**
 * 確定済みシフトタブの1ページあたりの表示件数
 *
 * @description
 * 一覧はサーバーサイドでページングするため、この件数をそのままAPIへ渡す。
 * 1年分（12ヶ月）が1ページに収まる件数として12件を採用する。
 */
export const CONFIRMED_SHIFT_MONTHS_ROWS_PER_PAGE = 12;

/**
 * useConfirmedShiftMonths フックの戻り値型
 *
 * @property {MonthlyShiftSlotSummary[]} months - 現在ページの月次シフト枠サマリー
 * @property {number} page - 現在のページ番号（0始まり）
 * @property {number} rowsPerPage - 1ページあたりの表示件数
 * @property {number} totalCount - 全件数
 * @property {function} setPage - ページ番号を変更する
 * @property {boolean} isLoading - ローディング状態
 * @property {string|null} error - エラーメッセージ
 * @property {function} reload - 現在ページを再取得する
 * @property {function} clearError - エラーメッセージをクリアする
 */
export interface UseConfirmedShiftMonthsReturn {
  months: MonthlyShiftSlotSummary[];
  page: number;
  rowsPerPage: number;
  totalCount: number;
  setPage: (page: number) => void;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  clearError: () => void;
}

/**
 * 確定済みシフトタブ用カスタムフック
 *
 * @description
 * 月次シフト枠のサマリー一覧（年月・ステータス）を取得する。
 * 解放枠を設定できるのはステータスが「確定」の月のみだが、
 * 他の月も進捗が分かるよう一覧にはすべてのステータスを表示する。
 * ページングはAPI側で行うため、ページ変更のたびに再取得する。
 *
 * @param {MonthlyShiftSlotSummaryRepository} repository - DIされるRepository実装
 * @returns {UseConfirmedShiftMonthsReturn} 一覧・ページング・状態
 */
export const useConfirmedShiftMonths = (
  repository: MonthlyShiftSlotSummaryRepository
): UseConfirmedShiftMonthsReturn => {
  const [months, setMonths] = useState<MonthlyShiftSlotSummary[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (targetPage: number) => {
      setIsLoading(true);
      setError(null);
      try {
        const { result } = await getMonthlyShiftSlotSummaryList(repository, {
          // APIのページ番号は1始まり、MUIのTablePaginationは0始まり
          page: targetPage + 1,
          pageSize: CONFIRMED_SHIFT_MONTHS_ROWS_PER_PAGE,
        });
        setMonths(result.data);
        setTotalCount(result.meta.total);
      } catch (e) {
        setError(e instanceof Error ? e.message : '月次シフトの取得に失敗しました');
        setMonths([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [repository]
  );

  useEffect(() => {
    load(page);
  }, [load, page]);

  const reload = useCallback(() => load(page), [load, page]);

  const clearError = useCallback(() => setError(null), []);

  return {
    months,
    page,
    rowsPerPage: CONFIRMED_SHIFT_MONTHS_ROWS_PER_PAGE,
    totalCount,
    setPage,
    isLoading,
    error,
    reload,
    clearError,
  };
};
